/**
 * Leviathan Web - Statement Print Page
 * Quasar Prime LLC
 *
 * Print-friendly statement view
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import { API_URL } from '@/config/api';

interface PrintData {
  statement: {
    id: string;
    statementNumber: string;
    periodStart: string;
    periodEnd: string;
    statementDate: string;
    dueDate: string | null;
    previousBalance: number;
    totalCharges: number;
    totalCredits: number;
    endingBalance: number;
    current: number;
    over30: number;
    over60: number;
    over90: number;
    customer: {
      id: string;
      customerNumber: string;
      name: string;
      address: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
    };
  };
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    total: number;
  }>;
  payments: Array<{
    id: string;
    paymentNumber: string;
    paymentDate: string;
    amount: number;
    paymentMethod: string;
  }>;
  tenant: {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  } | null;
}

/**
 * Statement Print Page
 */
const StatementPrint = () => {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const [data, setData] = useState<PrintData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (data) {
      // Auto-print after a short delay
      setTimeout(() => window.print(), 500);
    }
  }, [data]);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/statements/${id}/print`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch statement:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-gray-500">Loading statement...</div>
      </div>
    );
  }

  const { statement, invoices, payments, tenant } = data;

  return (
    <div className="print-container bg-white text-black p-8 max-w-4xl mx-auto">
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white;
          }
          .no-print { display: none !important; }
          .print-container { padding: 0; max-width: none; }
        }
        @page {
          size: letter;
          margin: 0.5in;
        }
        body {
          font-family: Arial, sans-serif;
        }
      `}</style>

      {/* Print/Close Buttons */}
      <div className="no-print mb-4 flex justify-end space-x-2">
        <button
          onClick={() => window.print()}
          className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded font-mono text-sm"
        >
          Print
        </button>
        <button
          onClick={() => window.close()}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-mono text-sm"
        >
          Close
        </button>
      </div>

      {/* Header */}
      <div className="flex justify-between mb-8 border-b-2 border-black pb-4">
        <div>
          <h1 className="text-2xl font-bold">{tenant?.name || 'Leviathan Fuel Distribution'}</h1>
          {tenant?.address && (
            <div className="text-sm text-gray-600 mt-1">
              <div>{tenant.address}</div>
              {tenant.city && (
                <div>
                  {tenant.city}, {tenant.state} {tenant.zip}
                </div>
              )}
              {tenant.phone && <div>{tenant.phone}</div>}
            </div>
          )}
        </div>
        <div className="text-right">
          <h2 className="text-xl font-bold uppercase tracking-wider">Statement</h2>
          <div className="text-sm mt-2">
            <div className="font-mono font-bold text-lg">{statement.statementNumber}</div>
            <div>Date: {formatDate(statement.statementDate)}</div>
          </div>
        </div>
      </div>

      {/* Customer & Period Info */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div className="border border-gray-300 p-4">
          <div className="text-xs font-bold uppercase border-b border-gray-300 pb-1 mb-3 text-gray-600">
            Bill To
          </div>
          <div className="font-bold text-lg">{statement.customer.name}</div>
          <div className="text-sm text-gray-600">{statement.customer.customerNumber}</div>
          {statement.customer.address && (
            <div className="text-sm mt-2">
              <div>{statement.customer.address}</div>
              <div>
                {statement.customer.city}, {statement.customer.state} {statement.customer.zip}
              </div>
            </div>
          )}
        </div>
        <div className="border border-gray-300 p-4">
          <div className="text-xs font-bold uppercase border-b border-gray-300 pb-1 mb-3 text-gray-600">
            Statement Period
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-gray-600">From:</div>
            <div>{formatDate(statement.periodStart)}</div>
            <div className="text-gray-600">To:</div>
            <div>{formatDate(statement.periodEnd)}</div>
            <div className="text-gray-600">Due Date:</div>
            <div className="font-bold">
              {statement.dueDate ? formatDate(statement.dueDate) : 'Upon Receipt'}
            </div>
          </div>
        </div>
      </div>

      {/* Account Summary */}
      <div className="border border-gray-300 mb-8">
        <div className="bg-gray-100 p-3 font-bold border-b border-gray-300">Account Summary</div>
        <div className="p-4">
          <div className="grid grid-cols-2 gap-2">
            <div>Previous Balance:</div>
            <div className="text-right font-mono">{formatCurrency(statement.previousBalance)}</div>
            <div>New Charges:</div>
            <div className="text-right font-mono">{formatCurrency(statement.totalCharges)}</div>
            <div>Payments Received:</div>
            <div className="text-right font-mono text-green-700">
              ({formatCurrency(statement.totalCredits)})
            </div>
            <div className="font-bold border-t border-gray-300 pt-2 text-lg">Amount Due:</div>
            <div className="text-right font-mono font-bold border-t border-gray-300 pt-2 text-xl">
              {formatCurrency(statement.endingBalance)}
            </div>
          </div>
        </div>
      </div>

      {/* Aging Summary */}
      <div className="border border-gray-300 mb-8">
        <div className="bg-gray-100 p-3 font-bold border-b border-gray-300">Aging Summary</div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-300 bg-gray-50">
              <th className="p-3 text-center text-sm">Current</th>
              <th className="p-3 text-center text-sm">31-60 Days</th>
              <th className="p-3 text-center text-sm">61-90 Days</th>
              <th className="p-3 text-center text-sm">Over 90 Days</th>
              <th className="p-3 text-center text-sm font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-3 text-center font-mono">{formatCurrency(statement.current)}</td>
              <td className="p-3 text-center font-mono">{formatCurrency(statement.over30)}</td>
              <td className="p-3 text-center font-mono">{formatCurrency(statement.over60)}</td>
              <td className="p-3 text-center font-mono text-red-600">
                {formatCurrency(statement.over90)}
              </td>
              <td className="p-3 text-center font-mono font-bold">
                {formatCurrency(statement.endingBalance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Transaction Detail */}
      <div className="border border-gray-300 mb-8">
        <div className="bg-gray-100 p-3 font-bold border-b border-gray-300">
          Transaction Detail
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-300 bg-gray-50">
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-right">Charges</th>
              <th className="p-2 text-right">Credits</th>
              <th className="p-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {/* Previous Balance */}
            <tr className="border-b border-gray-200">
              <td className="p-2">{formatDate(statement.periodStart)}</td>
              <td className="p-2">Previous Balance</td>
              <td className="p-2 text-right"></td>
              <td className="p-2 text-right"></td>
              <td className="p-2 text-right font-mono">
                {formatCurrency(statement.previousBalance)}
              </td>
            </tr>

            {/* Invoices */}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-gray-200">
                <td className="p-2">{formatDate(inv.invoiceDate)}</td>
                <td className="p-2">Invoice {inv.invoiceNumber}</td>
                <td className="p-2 text-right font-mono">{formatCurrency(inv.total)}</td>
                <td className="p-2 text-right"></td>
                <td className="p-2 text-right"></td>
              </tr>
            ))}

            {/* Payments */}
            {payments.map((pmt) => (
              <tr key={pmt.id} className="border-b border-gray-200">
                <td className="p-2">{formatDate(pmt.paymentDate)}</td>
                <td className="p-2">
                  Payment {pmt.paymentNumber} - {pmt.paymentMethod}
                </td>
                <td className="p-2 text-right"></td>
                <td className="p-2 text-right font-mono text-green-700">
                  {formatCurrency(pmt.amount)}
                </td>
                <td className="p-2 text-right"></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-bold bg-gray-100">
              <td className="p-2" colSpan={2}>
                Ending Balance
              </td>
              <td className="p-2 text-right font-mono">
                {formatCurrency(statement.totalCharges)}
              </td>
              <td className="p-2 text-right font-mono text-green-700">
                {formatCurrency(statement.totalCredits)}
              </td>
              <td className="p-2 text-right font-mono text-lg">
                {formatCurrency(statement.endingBalance)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-600 border-t border-gray-300 pt-4">
        <p className="font-semibold">
          Please remit payment by{' '}
          {statement.dueDate ? formatDate(statement.dueDate) : 'upon receipt'}.
        </p>
        {tenant?.phone && <p className="mt-1">Questions? Contact us at {tenant.phone}</p>}
        <p className="mt-4 text-xs text-gray-400">
          Thank you for your business!
        </p>
      </div>
    </div>
  );
};

export default StatementPrint;
